import {
    AnimationComponent,
    Animation,
    Component,
    Object3D,
    TextComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {SpecialPower} from '../special-power.js';
import {Puzzle} from '../puzzle.js';
import {ScoreController, ScoreType} from '../score-controller.js';
import {SpecialBombType} from '../../enums/SpecialBombType.js';
import {CameraShakeParameters} from '../../classes/CameraShakeParameters.js';
import {Sounds} from '../../enums/Sounds.js';
import {AudioDirector} from '../audio-director.js';
import {Statistics} from '../statistics.js';
import {StatNames} from '../../enums/StatNames.js';
import {FXPrefabs} from '../fx-prefabs.js';
import {vec3} from 'gl-matrix';
import {WLUtils} from '../../utils/WLUtils.js';
import {SpawnScoreAfterSeconds} from '../spawn-score-after-seconds.js';
import {BoardColor} from '../board-object.js';

export const tempVec = vec3.create();

export class TntPower extends SpecialPower {
    static TypeName = 'tnt-power';
    static InheritProperties = true;

    @property.int(2)
    explosionRadius = 2;

    cameraShakeParameters: CameraShakeParameters = new CameraShakeParameters(
        6,
        3,
        0.067,
        12
    );

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))))
    explodeSFX: Sounds;

    @property.object({required: true})
    animatedObject!: Object3D;

    @property.animation()
    idle?: Animation;

    @property.string('')
    scorePrefab = '';

    private _animation: AnimationComponent;

    start(): void {
        this._animation = this.animatedObject.getComponent(AnimationComponent);

        this._animation.animation = this.idle.retarget([
            this.animatedObject,
            this.animatedObject,
        ]);

        this._animation.play();
    }

    override activate(puzzle: Puzzle, row: number, column: number) {
        // do not call super, because there's no activation
    }

    override explode(puzzle: Puzzle, row: number, column: number): void {
        Statistics.instance.recordAdditive(StatNames.TNTExploded, 1);
        if (this.explodeSFX) {
            AudioDirector.instance.playOneShotWithPriority(this.explodeSFX);
        }
        // // called when this object explodes on the board.
        // if (deathFX != null)
        // {
        // 	for (int i = 0; i< deathFX.Length; i++)
        // 	{
        // 		Instantiate(deathFX[i], transform.position, puzzle.Lanes[column].transform.rotation);
        // 	}
        // }
        let fruitCount = 0;
        let chainCount = 0;
        let loadedCount = 0;
        for (let y = -this.explosionRadius; y <= this.explosionRadius; y++) {
            for (let x = -this.explosionRadius; x <= this.explosionRadius; x++) {
                const distance2 = x * x + y * y;
                if (distance2 <= this.explosionRadius * this.explosionRadius) {
                    let {validRow, validCol} = puzzle.validateCell(row + x, column + y);
                    if (validRow >= 0 && validCol >= 0) {
                        const isSomething = puzzle.getObject(validRow, validCol);
                        if (isSomething != null) {
                            if (isSomething.isFruit) {
                                fruitCount++;
                            } else {
                                chainCount++;
                                if (isSomething.loaded) {
                                    loadedCount++;
                                }
                            }
                            if (!puzzle.isRegisteredForActivation(validRow, validCol)) {
                                puzzle.registerForPop(validRow, validCol, 0, 0);
                            }
                        }
                    }
                }
            }
        }
        if (this.scorePrefab) {
            const targetCamera = this.engine.scene.activeViews[0].object;

            const tntScore = FXPrefabs.instance.spawn(this.scorePrefab, targetCamera);
            this.object.getPositionWorld(tempVec);
            tntScore.setPositionWorld(tempVec);

            const tntParams = tntScore.getComponent(SpawnScoreAfterSeconds);

            tntParams.setTextOfThis('');

            const newScore = puzzle.rules.calculateScore(
                chainCount - loadedCount,
                loadedCount,
                fruitCount,
                SpecialBombType.TNT
            );
            tntParams.setScoreToShow(newScore);
            tntParams.setParent(targetCamera);
            tntParams.setColor(BoardColor.Red);
            WLUtils.setActive(tntScore, true);
        } else {
            const newScore = puzzle.rules.calculateScore(
                chainCount - loadedCount,
                loadedCount,
                fruitCount,
                SpecialBombType.TNT
            );
            puzzle.addScore(
                newScore,
                this.object,
                ScoreType.Score,
                chainCount + fruitCount,
                false
            );
        }
        puzzle.mainCamera.shakeCamera(this.cameraShakeParameters);
        // if (AttachToBaseFX != null)
        // {
        // 	Destroy(AttachToBaseFX);
        // }
        this.object.destroy();
    }
}
